import {Navigate, Outlet, Route, useLocation} from "react-router-dom";
import {LoadingAnim} from "./tools/CommonHtml";
import {useFeatureFlags} from "./app/UseAppData";
import {routes} from "./routes";
import {routes as v3Routes} from "./v3/routes";

// Hosts a rewritten v3 page inside the v1 app under a /v3 prefix, one feature flag per page — for a
// page that needs no new wrappers. Rewriting the wrappers themselves happens in src/v3/App.tsx behind
// VITE_FF_UI_V3 instead. Nothing under src/v3/ imports this file: at cutover v3 replaces src/ and
// this whole seam is deleted.

export const V3_PREFIX = "/v3";
export const stripV3 = path => path.startsWith(`${V3_PREFIX}/`)? path.slice(V3_PREFIX.length) : path;

const isFfOn = (ftrFlags, ff) => !!ftrFlags?.[ff]?.enabled;

// A twin inherits its v1 entry for whatever v3 hasn't declared yet, so name/tName/defs keep driving
// the title and the sidebar anchor mid-rewrite. Comp comes from v3 alone — inheriting it would serve
// the v1 page at the twin URL, the one thing a twin must never do.
export const buildV3Twins = (v1, v3) => Object.fromEntries(
	Object.entries(v3).filter(([path]) => v1[path])
		.map(([path, rt]) => [V3_PREFIX + path, {...v1[path], ...rt, Comp: rt.Comp}]));

export const v3Twins = buildV3Twins(routes, v3Routes);

// nav.to() is the sole choke point that writes nav state and the URL together, but it runs outside
// React and above the auth gates, so it can't read flags itself. V3FlagsProbe mounts once inside the
// gates and leaves them here for it.
let navFlags = null;
export const V3FlagsProbe = () => (navFlags = useFeatureFlags().rsp, null);

// The twin URL for a v1 path once its page is claimed and its flag is on, so every nav.to(ROUTES.X)
// in the app lands on the rewrite without touching its 200-odd call sites.
export const v3PathOf = path =>
	isFfOn(navFlags, v3Twins[V3_PREFIX + path]?.ff)? V3_PREFIX + path : path;

// Reads the flag off the entry rather than the module table, so an unclaimed path has no ff and falls
// back to v1 the same way a flag that's off does.
const V3Outlet = ({routes}) => {
	const {pathname} = useLocation();
	const {rsp: ftrFlags} = useFeatureFlags();
	const v1Path = stripV3(pathname);

	// Flags only land after auth
	if (!ftrFlags)
		return <LoadingAnim classNameSize="w-5 h-5"/>;

	// display:contents so the v3 font scope adds no box — a real wrapper would break a page's h-full.
	return isFfOn(ftrFlags, routes[pathname]?.ff)? <div className="v3 contents"><Outlet/></div> : <Navigate replace to={v1Path}/>;
};

// Child paths are relative — a nested route may not start with "/".
// A twin with no Comp gets no element, so it falls through to an empty Outlet.
export const v3Route = routes =>
	<Route path={V3_PREFIX} element={<V3Outlet routes={routes}/>}>
		{Object.entries(routes).filter(([path]) => path.startsWith(V3_PREFIX)).map(([path, {Comp, instance}]) =>
			<Route key={path} path={path.slice(V3_PREFIX.length + 1)} element={Comp && <Comp key={path + (instance ?? 0)}/>}/>)}
	</Route>;
